import React, { useState } from 'react';
import { X, Download, Trash2, Archive, ChevronUp, ChevronDown, Waypoints, Send, Pencil, Clipboard, Check, Link, Maximize2, Minimize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStagingTray } from '../../context/StagingTrayContext';
import { useStagedEdit } from '../../context/StagedEditContext';
import { InkQuoteApi } from '../../services/inkquoteApi';
import TweetCard from './TweetCard';
import RedditCard from './RedditCard';
import RoamQuoteCard from './RoamQuoteCard';
import { isRoamSource } from '../utils';

const StagingTrayBottomPanel = () => {
  const { items, isOpen, setTrayOpen, clearTray, removeItem } = useStagingTray();
  const { addStagedEditItems } = useStagedEdit();
  const [exportFormat, setExportFormat] = useState('clipboard-text');
  const [isExpanded, setIsExpanded] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const navigate = useNavigate();

  const isApiConfigured = InkQuoteApi.isConfigured();

  const isClipboardFormat = exportFormat === 'clipboard-roam-uids' || exportFormat === 'clipboard-text' || exportFormat === 'clipboard-mixed';

  const toRoamDate = (dateStr) => {
    const d = new Date(dateStr);
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const day = d.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st'
      : day === 2 || day === 22 ? 'nd'
      : day === 3 || day === 23 ? 'rd'
      : 'th';
    return `[[${months[d.getMonth()]} ${day}${suffix}, ${d.getFullYear()}]]`;
  };

  const buildClipboardContent = () => {
    if (exportFormat === 'clipboard-roam-uids') {
      return items.map(item => {
        const uid = item.metadata?.original_id;
        return uid ? `- ((${uid}))` : `- ${item.text || ''}`;
      }).join('\n');
    }

    const buildTextBullet = (item) => {
      const text = item.text || '';
      const url = item.url || '';
      const author = item.user?.name || item.user?.screen_name || 'Unknown';
      const dateStr = toRoamDate(item.created_at);
      const icon = item.source === 'reddit' ? '🤖' : isRoamSource(item.source) ? '🌿' : '🐦';
      return `- > ${text}\n  [${icon}](${url}) by ${author} on ${dateStr}`;
    };

    if (exportFormat === 'clipboard-text') {
      return items.map(buildTextBullet).join('\n');
    }

    if (exportFormat === 'clipboard-mixed') {
      return items.map(item => {
        if (isRoamSource(item.source)) {
          const uid = item.metadata?.original_id;
          return uid ? `- ((${uid}))` : `- ${item.text || ''}`;
        }
        return buildTextBullet(item);
      }).join('\n');
    }

    return '';
  };

  const handleCopy = async () => {
    const content = buildClipboardContent();
    try {
      await navigator.clipboard.writeText(content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = content;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  if (!isOpen) return null;

  const handleExport = () => {
    const exportData = {
      exported_at: new Date().toISOString(),
      total_items: items.length,
      items: items.map(item => ({
        id: item.id,
        text: item.text,
        source: item.source,
        created_at: item.created_at,
        url: item.url,
        user: item.user,
        // Include source-specific data
        ...(item.source === 'reddit' && {
          subreddit: item.subreddit,
          author: item.author,
          ups: item.ups,
          downs: item.downs,
          num_comments: item.num_comments
        }),
        ...(isRoamSource(item.source) && {
          metadata: item.metadata
        }),
        ...(!isRoamSource(item.source) && item.source !== 'reddit' && {
          retweet_count: item.retweet_count,
          reply_count: item.reply_count,
          medias: item.medias
        })
      }))
    };

    let content, filename, mimeType;

    switch (exportFormat) {
      case 'json':
        content = JSON.stringify(exportData, null, 2);
        filename = `staging-tray-export-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
        break;
      case 'csv':
        const csvHeader = 'Source,Created At,Text,URL,User,Additional Info\n';
        const csvRows = items.map(item => {
          const additionalInfo = item.source === 'reddit' 
            ? `r/${item.subreddit}` 
            : isRoamSource(item.source)
            ? item.metadata?.page_title 
            : '';
          return `"${item.source}","${item.created_at}","${item.text.replace(/"/g, '""')}","${item.url}","${item.user?.name || item.user?.screen_name || ''}","${additionalInfo}"`;
        }).join('\n');
        content = csvHeader + csvRows;
        filename = `staging-tray-export-${new Date().toISOString().split('T')[0]}.csv`;
        mimeType = 'text/csv';
        break;
      case 'markdown':
        content = `# Staging Tray Export\n\nExported on: ${new Date().toISOString()}\nTotal items: ${items.length}\n\n`;
        content += items.map((item, index) => {
          let itemContent = `## ${index + 1}. ${item.source.charAt(0).toUpperCase() + item.source.slice(1)}\n\n`;
          itemContent += `**Created:** ${new Date(item.created_at).toLocaleDateString()}\n`;
          itemContent += `**User:** ${item.user?.name || item.user?.screen_name || 'Unknown'}\n`;
          if (item.source === 'reddit') {
            itemContent += `**Subreddit:** r/${item.subreddit}\n`;
          } else if (isRoamSource(item.source)) {
            itemContent += `**Page:** ${item.metadata?.page_title || 'Unknown'}\n`;
          }
          itemContent += `**URL:** ${item.url}\n\n`;
          itemContent += `${item.text}\n\n---\n\n`;
          return itemContent;
        }).join('');
        filename = `staging-tray-export-${new Date().toISOString().split('T')[0]}.md`;
        mimeType = 'text/markdown';
        break;
      default:
        return;
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFindSimilar = () => {
    if (items.length === 0) return;

    // Create comma-separated list of item IDs
    const itemIds = items.map(item => item.id).join(',');

    // Navigate to main search page with similar parameter
    navigate(`/?similar=${encodeURIComponent(itemIds)}`);

    // Optional: Close the tray after navigating
    setTrayOpen(false);
  };

  const handleShareLink = async () => {
    if (items.length === 0) return;
    const itemIds = items.map(item => item.id).join(',');
    const shareUrl = `${window.location.origin}/?staged=${encodeURIComponent(itemIds)}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setShareLinkCopied(true);
    setTimeout(() => setShareLinkCopied(false), 2000);
  };

  // Send all items to Manage tab for editing
  const handleSendAllToManage = () => {
    if (items.length === 0) return;

    addStagedEditItems(items);
    clearTray();
    setTrayOpen(false);
    navigate('/inkquote');
  };

  // Send a single item to Manage tab for editing
  const handleSendItemToManage = (item) => {
    addStagedEditItems([item]);
    removeItem(item);

    // If this was the last item, close the tray
    if (items.length === 1) {
      setTrayOpen(false);
    }

    navigate('/inkquote');
  };

  const renderCard = (item) => {
    switch (item.source) {
      case 'reddit':
        return <RedditCard key={item.id} post={item} />;
      case 'roam':
        return <RoamQuoteCard key={item.id} quote={item} />;
      default:
        return <TweetCard key={item.id} tweet={item} />;
    }
  };

  // In full screen the panel fills the viewport and the content area flexes to fill
  const showContent = isFullScreen || isExpanded;

  return (
    <div
      className={`fixed z-50 bg-white dark:bg-gray-900 shadow-lg ${
        isFullScreen
          ? 'inset-0 flex flex-col'
          : 'inset-x-0 bottom-0 border-t border-gray-200 dark:border-gray-700'
      }`}
    >
      {/* Header */}
      <div className="flex flex-wrap sm:flex-nowrap items-center p-4 border-b border-gray-200 dark:border-gray-700 gap-3">
        {/* Row 1 left (mobile) / leftmost (desktop) */}
        <div className="flex items-center gap-3 flex-1">
          <div className="flex items-center gap-2">
            <Archive size={20} className="text-gray-600 dark:text-gray-300" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Staged Saves</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">({items.length} items)</span>
          </div>

          {!isFullScreen && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          )}
        </div>

        {/* Window controls: row 1 right on mobile, far right on desktop */}
        <div className="order-1 sm:order-last flex items-center gap-1">
          <button
            onClick={() => setIsFullScreen(!isFullScreen)}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title={isFullScreen ? 'Exit full screen' : 'Full screen'}
          >
            {isFullScreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
          </button>

          <button
            onClick={() => setTrayOpen(false)}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Row 2 (mobile) / middle (desktop) */}
        <div className="order-2 sm:order-none flex items-center gap-2 w-full sm:w-auto">
          {/* Export Controls */}
          <div className="flex items-center gap-2 flex-1 sm:flex-none">
            <button
              onClick={handleFindSimilar}
              disabled={items.length === 0}
              className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
              title="Find saves similar to these items"
            >
              <Waypoints size={14} />
              <span className="hidden sm:inline">Find Similar</span>
            </button>

            <button
              onClick={handleShareLink}
              disabled={items.length === 0}
              className={`flex items-center gap-1 text-white px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors ${shareLinkCopied ? 'bg-green-600 hover:bg-green-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              title="Copy a shareable link that restores this tray on any device"
            >
              {shareLinkCopied ? <Check size={14} /> : <Link size={14} />}
              <span className="hidden sm:inline">{shareLinkCopied ? 'Copied!' : 'Share Link'}</span>
            </button>

            {isApiConfigured && (
              <button
                onClick={handleSendAllToManage}
                disabled={items.length === 0}
                className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
                title="Send all items to Manage tab for editing"
              >
                <Send size={14} />
                <span className="hidden sm:inline">Send all to Manage</span>
              </button>
            )}

            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 flex-1 sm:flex-none"
            >
              <option value="json">JSON</option>
              <option value="csv">CSV</option>
              <option value="markdown">Markdown</option>
              <option value="clipboard-text">Copy: Text</option>
              <option value="clipboard-roam-uids">Copy: Roam UIDs</option>
              <option value="clipboard-mixed">Copy: Mixed</option>
            </select>

            {isClipboardFormat ? (
              <button
                onClick={handleCopy}
                disabled={items.length === 0}
                className={`flex items-center gap-1 text-white px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors ${copySuccess ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {copySuccess ? <Check size={14} /> : <Clipboard size={14} />}
                <span className="hidden sm:inline">{copySuccess ? 'Copied!' : 'Copy'}</span>
              </button>
            ) : (
              <button
                onClick={handleExport}
                disabled={items.length === 0}
                className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
              >
                <Download size={14} />
                <span className="hidden sm:inline">Export</span>
              </button>
            )}
          </div>
          
          <button
            onClick={clearTray}
            disabled={items.length === 0}
            className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
          >
            <Trash2 size={14} />
            <span className="hidden sm:inline">Clear</span>
          </button>
        </div>
      </div>

      {/* Content */}
      {showContent && (
        <div
          className={`overflow-y-auto p-4 bg-gray-50 dark:bg-gray-800/50 ${
            isFullScreen ? 'flex-1' : 'max-h-96'
          }`}
        >
          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Archive size={48} className="mx-auto mb-4 opacity-50" />
              <p>No items in tray</p>
              <p className="text-sm mt-2">Click on any save to add it to your tray</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item, index) => (
                <div key={item.id} className="relative">
                  <div className="absolute top-2 right-2 z-10 flex gap-1">
                    {isApiConfigured && (
                      <button
                        onClick={() => handleSendItemToManage(item)}
                        className="bg-purple-500 hover:bg-purple-600 text-white rounded-full p-1 shadow-md transition-colors"
                        title="Send to Manage tab for editing"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                  </div>
                  {renderCard(item)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Collapsed preview */}
      {!showContent && items.length > 0 && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {items.slice(0, 5).map((item) => (
              <div key={item.id} className="flex-shrink-0 w-48 h-24 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 p-2 overflow-hidden">
                <div className="text-xs text-gray-500 dark:text-gray-400 capitalize mb-1">
                  {item.source}
                </div>
                <div className="text-sm text-gray-900 dark:text-gray-100 line-clamp-3">
                  {item.text.substring(0, 100)}...
                </div>
              </div>
            ))}
            {items.length > 5 && (
              <div className="flex-shrink-0 w-48 h-24 bg-gray-100 dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  +{items.length - 5} more
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StagingTrayBottomPanel;